/**
 * The query string a researcher puts on their external invite link.
 *
 * `uid` is ours (one completion per uid per project). Anything else is the
 * researcher's own: a wave, a source, their customer id. It is sent with the
 * application, frozen onto it by core-api as `externalLinkParams`, and handed
 * back to the researcher on the participant and in the export.
 *
 * Read from the raw query string, never from the router's parsed search:
 * the router JSON-parses each value, which turns `uid=42` into a number
 * (and `wave=007` into `7`, `id=1e3` into `1000`), and a researcher's id is
 * whatever they wrote.
 *
 * The limits mirror core-api's (`external-participants/external-link-params.ts`),
 * which refuses anything outside them: a key is a short identifier, never a
 * Mongo path or operator; a value is a short string. Anything outside them
 * is dropped here rather than failing the landing page, since the person
 * holding the link did not write it.
 */

import { z } from 'zod';

export const MAX_EXTERNAL_LINK_PARAMS = 20;
export const MAX_EXTERNAL_LINK_PARAM_KEY_LENGTH = 64;
export const MAX_EXTERNAL_LINK_PARAM_VALUE_LENGTH = 256;
export const MAX_EXTERNAL_LINK_UID_LENGTH = 256;

const KEY_PATTERN = /^[A-Za-z0-9_-]+$/;

/**
 * `uid` is the link flow's own; the other three mean something to a
 * JavaScript object and could reach a prototype through a careless merge.
 */
const RESERVED_KEYS = new Set(['uid', '__proto__', 'constructor', 'prototype']);

export interface ExternalLinkSearch {
  uid?: string;
  params: Record<string, string>;
}

export function isExternalLinkParamKey(key: string): boolean {
  return (
    !RESERVED_KEYS.has(key) &&
    key.length <= MAX_EXTERNAL_LINK_PARAM_KEY_LENGTH &&
    KEY_PATTERN.test(key)
  );
}

/**
 * The uid and the researcher's own parameters in a landing page's raw query
 * string (`location.searchStr`, with or without the leading `?`), trimmed
 * to the limits. The first of a repeated parameter wins; keys keep the
 * order given, up to the cap.
 */
export function parseExternalLinkQuery(searchStr: string): ExternalLinkSearch {
  const query = new URLSearchParams(searchStr);

  const rawUid = query.get('uid')?.trim();
  const uid =
    rawUid && rawUid.length > 0 && rawUid.length <= MAX_EXTERNAL_LINK_UID_LENGTH
      ? rawUid
      : undefined;

  // No prototype, so no key can ever be read back from one.
  const params: Record<string, string> = Object.create(null);
  for (const [key, value] of query) {
    if (Object.keys(params).length >= MAX_EXTERNAL_LINK_PARAMS) break;
    if (key in params || !isExternalLinkParamKey(key)) continue;
    if (value.length > MAX_EXTERNAL_LINK_PARAM_VALUE_LENGTH) continue;
    params[key] = value;
  }

  return uid ? { uid, params } : { params };
}

/**
 * The researcher's parameters as the submit route accepts them in a request
 * body: the limits {@link parseExternalLinkQuery} trims to.
 */
export const externalLinkParamsSchema = z
  .record(z.string(), z.string().max(MAX_EXTERNAL_LINK_PARAM_VALUE_LENGTH))
  .refine(
    (params) => {
      const keys = Object.keys(params);
      return (
        keys.length <= MAX_EXTERNAL_LINK_PARAMS &&
        keys.every((key) => isExternalLinkParamKey(key))
      );
    },
    { message: 'Invalid link parameters' },
  );
